import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import ProxyPageOverrideContext from "arena/components/ProxyPage/ProxyPageOverrideContext";
import Channel from "arena/components/Channel";
import Video from "arena/components/Video";
import Stream from "arena/components/Stream";
import Product from "arena/components/Product";
import Post from "arena/components/Post";
import DigitalAsset from "arena/components/DigitalAsset";
import { useArena } from "core/components/ArenaProvider";
import { useIsRouteDialogOpen } from "arena/components/RouteDialog/RouteDialogOpenContext";
import { buildProxyPageOverride } from "arena/components/ProxyPage/proxyPageOverride";

const CHANNEL = "CHANNEL";
const VIDEO = "VIDEO";
const STREAM = "STREAM";

function ProxyPageContent({ arenaProxyPage, suspended }) {
  const { content, product, post, digitalAsset, tag } = arenaProxyPage;

  if (tag) {
    return <Navigate to={new URL(tag.url).pathname} replace />;
  }

  if (content?.type === CHANNEL && content?.channel?.slug) {
    return (
      <Channel params={{ slug: content.channel.slug }} suspended={suspended} />
    );
  }

  if (content?.type === VIDEO) {
    return <Video params={{ contentId: content.id }} suspended={suspended} />;
  }

  if (content?.type === STREAM) {
    return <Stream params={{ contentId: content.id }} suspended={suspended} />;
  }

  if (product) {
    return <Product params={{ productId: product.id }} />;
  }

  if (post) {
    return <Post params={{ postId: post.id }} />;
  }

  if (digitalAsset) {
    return <DigitalAsset params={{ digitalAssetId: digitalAsset.id }} />;
  }

  return null;
}

ProxyPageContent.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
  suspended: PropTypes.bool,
};

/**
 * Прокси-страница: внешний адрес арены (матч на sports.ru и подобные),
 * за которым закреплён канал, видео, стрим, продукт, пост или файл.
 *
 * Рисуется обычной страницей, а не выдвижной панелью. Остальной контент
 * арены живёт в RouteDialog, потому что открывается поверх текущей
 * страницы и не должен терять её прокрутку. У прокси-страницы такого
 * сценария нет: на неё приходят по прямой ссылке извне, позади ничего
 * нет, и закрывать её некуда. Обёртка модальным окном при этом забирала
 * прокрутку у документа: панель заводила свой контейнер, из-за чего
 * приходилось руками повторять отступ под шапку, а на мобильных
 * включался полноэкранный режим, в котором содержимое ниже плеера было
 * недостижимо вовсе.
 *
 * Прокручивается документ, поэтому сами собой работают вещи, которые
 * модальное окно ломало: шапка прячется при прокрутке, на iOS
 * сворачивается адресная строка, работают restoration позиции и sticky
 * внутри содержимого, доступен футер. Отступ под фиксированную шапку уже
 * держит AppShell.
 */
export default function ProxyPage({ arenaProxyPage }) {
  const arena = useArena();
  /**
   * Поверх прокси-страницы может открыться RouteDialog (следующее видео
   * плейлиста передаёт background в state, и страница остаётся
   * смонтированной под диалогом). Её плеер продолжал бы играть — ставим
   * его на паузу через suspended, как это сделано в ArenaTv.
   */
  const isRouteDialogOpen = useIsRouteDialogOpen();

  const overrideValue = useMemo(
    () => buildProxyPageOverride(arenaProxyPage, arena),
    [arenaProxyPage, arena]
  );

  return (
    <ProxyPageOverrideContext.Provider value={overrideValue}>
      <ProxyPageContent
        arenaProxyPage={arenaProxyPage}
        suspended={isRouteDialogOpen}
      />
    </ProxyPageOverrideContext.Provider>
  );
}

ProxyPage.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
};
